import { call, put, takeLatest } from "redux-saga/effects";
import { partnerProgramBenefitsSliceAction } from "../../../slices/partner-program/benefits";
import {
  PartnerProgramBenefitsListType,
  PartnerProgramBenefitsResponseType,
} from "../../../slices/partner-program/benefits/Types";
import {
  DeleteBenefitCategoryPayloadType,
  DeleteBenefitPayloadType,
  GetBenefitsByTierPayloadType,
  InsertBenefitsPayloadType,
  getPartnerProgramBenefitsSagaAction,
  partnerProgramBenefitsSagaActionTypes,
} from "./saga-actions";
import { axiosRestInstance } from "../../../../common/axios";

const getPartnerProgramBenefitsAPI = async () => {
  const response = await axiosRestInstance.get<PartnerProgramBenefitsResponseType>(
    "/partner-program/tiers/benefits/getBenefits"
  );
  return response.data;
};

const getBenefitsByTierAPI = async (tierId: number, benefitsType?: string) => {
  const response = await axiosRestInstance.get<PartnerProgramBenefitsResponseType>(
    "/partner-program/tiers/benefits/getBenefits",
    {
      params: {
        tier_id: tierId,
        ...(benefitsType ? { benefits_type: benefitsType } : {}),
      },
    }
  );
  return response.data;
};

type InsertBenefitsAPIResponseType = {
  success: boolean;
  message: string;
};

const insertPartnerProgramBenefitsAPI = async (
  payload: Omit<InsertBenefitsPayloadType, "onSuccess" | "onError">
) => {
  const response = await axiosRestInstance.post<InsertBenefitsAPIResponseType>(
    "/partner-program/tiers/InsertBenefit",
    payload
  );
  return response.data;
};

const deletePartnerProgramBenefitCategoryAPI = async (
  payload: Omit<DeleteBenefitCategoryPayloadType, "onSuccess" | "onError">
) => {
  const response = await axiosRestInstance.delete<InsertBenefitsAPIResponseType>(
    "/partner-program/tiers/category/deleteBenefitCategory",
    { data: payload }
  );
  return response.data;
};

const deletePartnerProgramBenefitAPI = async (
  payload: Omit<DeleteBenefitPayloadType, "onSuccess" | "onError">
) => {
  const response = await axiosRestInstance.delete<InsertBenefitsAPIResponseType>(
    `/partner-program/tiers/benefit/deleteBenefit/${payload.ID}`
  );
  return response.data;
};

const mapResponseToBenefits = (
  response: PartnerProgramBenefitsResponseType
): PartnerProgramBenefitsListType => {
  return (response.data || []).map((category, categoryIndex) => ({
    id: `category-${categoryIndex + 1}`,
    title: category.category_name,
    order: categoryIndex + 1,
    isCollapsed: false,
    benefits: (category.benefits || [])
      .map((benefit, benefitIndex) => ({
        id: `benefit-${benefit.ID}`,
        apiId: benefit.ID,
        name: benefit.benefits_name,
        description: benefit.description || "",
        order: Number(benefit.sort_order) || benefitIndex + 1,
        activeTierIds: benefit.tier_ids || [],
        benefitsType: benefit.benefits_type || "",
        benefitsStatus:
          benefit.benefits_status === true || Number(benefit.benefits_status) === 1
            ? 1
            : 0,
        deliveryType: benefit.delivery_type || "",
        weightage:
          benefit.weightage === null || benefit.weightage === undefined
            ? null
            : Number(benefit.weightage),
        frequency: benefit.frequency || "",
      }))
      .sort((a, b) => a.order - b.order)
      .map((benefit, index) => ({
        ...benefit,
        order: index + 1,
      })),
  }));
};

function* getPartnerProgramBenefitsListSaga() {
  try {
    yield put(partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsLoading(true));
    const response: PartnerProgramBenefitsResponseType = yield call(
      getPartnerProgramBenefitsAPI
    );
    const mappedList: PartnerProgramBenefitsListType = mapResponseToBenefits(response);
    yield put(partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsList(mappedList));
    yield put(
      partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsTierDetails(
        response.tierDetails || []
      )
    );
    yield put(partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsErrorMessage(""));
  } catch (e: any) {
    yield put(partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsList([]));
    yield put(partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsTierDetails([]));
    yield put(
      partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsErrorMessage(
        e?.message || "Unable to load partner program benefits."
      )
    );
  } finally {
    yield put(partnerProgramBenefitsSliceAction.setPartnerProgramBenefitsLoading(false));
  }
}

function* getBenefitsByTierSaga(action: {
  type: string;
  payload: GetBenefitsByTierPayloadType;
}) {
  const { tierId, benefitsType, onSuccess, onError } = action.payload;
  try {
    const response: PartnerProgramBenefitsResponseType = yield call(
      getBenefitsByTierAPI,
      tierId,
      benefitsType
    );
    const options = (response.data || []).flatMap((category) =>
      (category.benefits || []).map((benefit) => ({
        label: benefit.benefits_name,
        value: String(benefit.ID),
        id: benefit.ID,
      }))
    );
    if (onSuccess) {
      onSuccess(options);
    }
  } catch (e: any) {
    if (onError) {
      onError(
        e?.response?.data?.message || e?.message || "Unable to load benefits."
      );
    }
  }
}

function* insertPartnerProgramBenefitsSaga(action: {
  type: string;
  payload: InsertBenefitsPayloadType;
}) {
  try {
    yield put(partnerProgramBenefitsSliceAction.setBenefitsSaveLoading(true));
    const { onSuccess, onError, ...requestPayload } = action.payload;
    const response: InsertBenefitsAPIResponseType = yield call(
      insertPartnerProgramBenefitsAPI,
      requestPayload
    );
    if (response?.success === false) {
      if (onError) {
        onError(response?.message || "Unable to update benefits.");
      }
      return;
    }
    yield put(getPartnerProgramBenefitsSagaAction());
    if (onSuccess) {
      onSuccess(response?.message || "Benefits updated successfully.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to update benefits."
      );
    }
  } finally {
    yield put(partnerProgramBenefitsSliceAction.setBenefitsSaveLoading(false));
  }
}

function* deletePartnerProgramBenefitCategorySaga(action: {
  type: string;
  payload: DeleteBenefitCategoryPayloadType;
}) {
  const { onSuccess, onError, ...requestPayload } = action.payload;
  try {
    const response: InsertBenefitsAPIResponseType = yield call(
      deletePartnerProgramBenefitCategoryAPI,
      requestPayload
    );
    yield put(getPartnerProgramBenefitsSagaAction());
    if (onSuccess) {
      onSuccess(response?.message || "Category deleted successfully.");
    }
  } catch (e: any) {
    if (onError) {
      onError(
        e?.response?.data?.message || "Unable to delete category."
      );
    }
  }
}

function* deletePartnerProgramBenefitSaga(action: {
  type: string;
  payload: DeleteBenefitPayloadType;
}) {
  const { onSuccess, onError, ...requestPayload } = action.payload;
  try {
    const response: InsertBenefitsAPIResponseType = yield call(
      deletePartnerProgramBenefitAPI,
      requestPayload
    );
    yield put(getPartnerProgramBenefitsSagaAction());
    if (onSuccess) {
      onSuccess(response?.message || "Benefit deleted successfully.");
    }
  } catch (e: any) {
    if (onError) {
      onError(
        e?.response?.data?.message || "Unable to delete benefit."
      );
    }
  }
}

function* partnerProgramBenefitsSaga() {
  yield takeLatest(
    partnerProgramBenefitsSagaActionTypes.callPartnerProgramBenefitsSaga,
    getPartnerProgramBenefitsListSaga
  );
  yield takeLatest(
    partnerProgramBenefitsSagaActionTypes.callGetBenefitsByTierSaga,
    getBenefitsByTierSaga
  );
  yield takeLatest(
    partnerProgramBenefitsSagaActionTypes.callInsertPartnerProgramBenefitsSaga,
    insertPartnerProgramBenefitsSaga
  );
  yield takeLatest(
    partnerProgramBenefitsSagaActionTypes.callDeletePartnerProgramBenefitCategorySaga,
    deletePartnerProgramBenefitCategorySaga
  );
  yield takeLatest(
    partnerProgramBenefitsSagaActionTypes.callDeletePartnerProgramBenefitSaga,
    deletePartnerProgramBenefitSaga
  );
}

export default partnerProgramBenefitsSaga;
